import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ListChecks, Broadcast, Scales, CheckCircle, ArrowRight, Crown,
} from "@phosphor-icons/react";
import { ALL_TOOLS } from "../lib/toolCopy";

const ICON_FOR = {
  bulk: ListChecks,
  monitoring: Broadcast,
  reconcile: Scales,
};

const ACCENT_FOR = {
  bulk: {
    chip: "bg-teal/10 text-teal",
    border: "border-teal/30",
    headline: "text-teal",
    tier: "Pro +",
  },
  monitoring: {
    chip: "bg-violet-100 text-violet-700",
    border: "border-violet-200",
    headline: "text-violet-700",
    tier: "Pro +",
  },
  reconcile: {
    chip: "bg-amber-100 text-amber-700",
    border: "border-amber-200",
    headline: "text-amber-700",
    tier: "All plans",
  },
};

/**
 * Homepage "Built for credentialing teams" showcase.
 * Tabbed interface surfacing the 3 flagship Pro/Team features to visitors
 * who land on the basic NPI lookup homepage and might not know these exist.
 */
export default function ProFeaturesShowcase() {
  const [active, setActive] = useState(ALL_TOOLS[0].key);
  const tool = ALL_TOOLS.find(t => t.key === active) || ALL_TOOLS[0];
  const Icon = ICON_FOR[tool.key];
  const accent = ACCENT_FOR[tool.key];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50" data-testid="pro-features-showcase">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section intro */}
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-navy/5 text-navy px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase mb-4">
            <Crown className="w-3.5 h-3.5 text-amber-500" weight="fill" />
            Built for credentialing teams
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-slate-900 mb-4 tracking-tight">
            More than a lookup — a compliance operating system
          </h2>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
            Free NPI lookups are just the start. Paid tiers unlock the tools credentialing coordinators at MSOs, staffing firms, and credentialing agencies use to keep 200–5,000 providers compliant every single day.
          </p>
        </div>

        {/* Tab buttons */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="pro-features-tabs">
          {ALL_TOOLS.map(t => {
            const TabIcon = ICON_FOR[t.key];
            const isActive = t.key === active;
            const a = ACCENT_FOR[t.key];
            return (
              <button
                key={t.key}
                onClick={() => setActive(t.key)}
                data-testid={`pro-feature-tab-${t.key}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                  isActive
                    ? "bg-navy text-white shadow-lg shadow-navy/20"
                    : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:text-slate-900"
                }`}
              >
                <TabIcon className="w-4 h-4" weight={isActive ? "fill" : "regular"} />
                {t.title}
                <span className={`hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
                  isActive ? "bg-white/20 text-white" : a.chip
                }`}>
                  {a.tier}
                </span>
              </button>
            );
          })}
        </div>

        {/* Active tab content */}
        <div className={`bg-white rounded-3xl border-2 ${accent.border} overflow-hidden shadow-xl shadow-slate-200/50`}>
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Left: headline + bullets */}
            <div className="lg:col-span-3 p-8 lg:p-10">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${accent.chip} mb-5`}>
                <Icon className="w-4 h-4" weight="fill" />
                {tool.title}
              </div>

              <h3 className={`font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-4 leading-tight`}>
                {tool.tagline}
              </h3>

              <p className="text-slate-600 text-base leading-relaxed mb-6">
                {tool.shortDescription}
              </p>

              <ul className="space-y-2.5 mb-8">
                {tool.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${accent.headline}`} weight="fill" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/dashboard" data-testid={`pro-feature-cta-${tool.key}`}>
                  <span className="inline-flex items-center gap-2 bg-navy hover:bg-navy/90 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors">
                    Open in Dashboard <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
                <Link to="/pricing" className="text-sm text-slate-500 hover:text-slate-900 font-medium transition-colors">
                  See pricing →
                </Link>
              </div>
            </div>

            {/* Right: how-it-works steps */}
            <div className="lg:col-span-2 bg-slate-50/50 border-t lg:border-t-0 lg:border-l border-slate-100 p-8 lg:p-10">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-400 mb-5">
                How it works
              </p>
              <ol className="space-y-5">
                {tool.steps.map((s, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-semibold text-sm text-slate-900">{s.title}</p>
                      <p className="text-[13px] text-slate-500 leading-relaxed mt-0.5">{s.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
